import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Paper,
  Stack,
  Chip,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material'
import { 
  Add as AddIcon, 
  PushPin as PushPinIcon,
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material'
import { supabase } from '../../supabaseClient'
import { useAuth } from '../../contexts/AuthContext'
import CommunityLayout from '../../components/community/CommunityLayout'


function Announcements() {
  const { user } = useAuth()
  const [announcements, setAnnouncements] = useState([])
  const [loading, setLoading] = useState(true)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [profile, setProfile] = useState(null)
  const [error, setError] = useState('')
  const [menuAnchorEl, setMenuAnchorEl] = useState(null)
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null)

  useEffect(() => {
    fetchAnnouncements()
    if (user) fetchProfile()

    // Realtime subscription
    const channel = supabase
      .channel('announcements-channel')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'announcements' },
        handleAnnouncementChange
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [user])

  const fetchAnnouncements = async () => {
    try {
      const { data: announcementsData, error } = await supabase
        .from('announcements')
        .select('*')
        .order('is_pinned', { ascending: false })
        .order('created_at', { ascending: false })

      if (error) throw error

      // Fetch authors separately
      const authorIds = [...new Set(announcementsData.map(a => a.author_id))]
      const { data: authorsData } = await supabase
        .from('profiles')
        .select('id, full_name, role')
        .in('id', authorIds)

      const authorsMap = {}
      authorsData?.forEach(author => {
        authorsMap[author.id] = author
      })

      const announcementsWithAuthors = announcementsData.map(announcement => ({
        ...announcement,
        author: authorsMap[announcement.author_id],
      }))

      setAnnouncements(announcementsWithAuthors)
    } catch (error) {
      console.error('Error fetching announcements:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchProfile = async () => {
    try {
      const { data } = await supabase.from('profiles').select('role').eq('id', user.id).single()

      setProfile(data)
    } catch (error) {
      console.error('Error fetching profile:', error)
    }
  }

  const handleAnnouncementChange = payload => {
    if (payload.eventType === 'INSERT') {
      fetchAnnouncements()
    } else if (payload.eventType === 'UPDATE') {
      setAnnouncements(prev =>
        prev.map(a => (a.id === payload.new.id ? { ...a, ...payload.new } : a))
      )
    } else if (payload.eventType === 'DELETE') {
      setAnnouncements(prev => prev.filter(a => a.id !== payload.old.id))
    }
  }

  const handleCreateAnnouncement = async () => {
    if (!title.trim() || !body.trim()) {
      setError('Title and body are required')
      return
    }

    try {
      await supabase.from('announcements').insert([
        {
          title: title.trim(),
          body: body.trim(),
          author_id: user.id,
        },
      ])

      setDialogOpen(false)
      setTitle('')
      setBody('')
      setError('')
    } catch (error) {
      console.error('Error creating announcement:', error)
      setError(error.message)
    }
  }

  const handleDeleteAnnouncement = async announcementId => {
    if (window.confirm('Are you sure you want to delete this announcement?')) {
      try {
        const { error } = await supabase.from('announcements').delete().eq('id', announcementId)

        if (error) throw error

        setAnnouncements(prev => prev.filter(a => a.id !== announcementId))
      } catch (error) {
        console.error('Error deleting announcement:', error)
        alert('Failed to delete announcement')
      }
    }
  }

  const handlePinAnnouncement = async announcement => {
    try {
      const { error } = await supabase
        .from('announcements')
        .update({ is_pinned: !announcement.is_pinned })
        .eq('id', announcement.id)

      if (error) throw error

      setAnnouncements(prev =>
        prev.map(a => (a.id === announcement.id ? { ...a, is_pinned: !a.is_pinned } : a))
      )
    } catch (error) {
      console.error('Error updating announcement:', error)
      alert('Failed to update announcement')
    }
  }

  const canCreateAnnouncement = profile?.role === 'admin' || profile?.role === 'moderator'

  const formatDate = dateString => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
  }

  return (
    <CommunityLayout>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight={700} sx={{ color: 'text.primary' }}>
          Announcements
        </Typography>
        {canCreateAnnouncement && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setDialogOpen(true)}
            sx={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            New Announcement
          </Button>
        )}
      </Box>

      {loading ? (
        <Typography>Loading...</Typography>
      ) : (
        <Stack spacing={3}>
          {announcements.length === 0 ? (
            <Alert severity="info">No announcements yet.</Alert>
          ) : (
            announcements.map(announcement => (
              <Paper
                key={announcement.id}
                elevation={0}
                sx={{
                  p: 4,
                  border: announcement.is_pinned ? '2px solid' : '1px solid',
                  borderColor: announcement.is_pinned ? 'primary.main' : 'divider',
                  bgcolor: announcement.is_pinned ? 'primary.50' : 'background.paper',
                  position: 'relative',
                }}
              >
                {/* Admin Controls - Top Right */}
                {canCreateAnnouncement && (
                  <IconButton
                    size="small"
                    onClick={e => {
                      setMenuAnchorEl(e.currentTarget)
                      setSelectedAnnouncement(announcement)
                    }}
                    sx={{
                      position: 'absolute',
                      top: 16,
                      right: 16,
                    }}
                  >
                    <MoreVertIcon />
                  </IconButton>
                )}

                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  {announcement.is_pinned && <PushPinIcon color="primary" sx={{ fontSize: 20 }} />}
                  <Typography variant="h5" fontWeight={700}>
                    {announcement.title}
                  </Typography>
                  <Chip
                    label={announcement.author?.role || 'staff'}
                    size="small"
                    color="primary"
                    sx={{ ml: 'auto' }}
                  />
                </Stack>

                <Typography variant="body1" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
                  {announcement.body}
                </Typography>

                <Typography variant="caption" color="text.secondary">
                  Posted by {announcement.author?.full_name} on{' '}
                  {formatDate(announcement.created_at)}
                </Typography>
              </Paper>
            ))
          )}
        </Stack>
      )}

      {/* Admin Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={() => {
          setMenuAnchorEl(null)
          setSelectedAnnouncement(null)
        }}
      >
        <MenuItem
          onClick={() => {
            handlePinAnnouncement(selectedAnnouncement)
            setMenuAnchorEl(null)
            setSelectedAnnouncement(null)
          }}
        >
          <ListItemIcon>
            <PushPinIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            {selectedAnnouncement?.is_pinned ? 'Unpin' : 'Pin'} Announcement
          </ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleDeleteAnnouncement(selectedAnnouncement?.id)
            setMenuAnchorEl(null)
            setSelectedAnnouncement(null)
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete Announcement</ListItemText>
        </MenuItem>
      </Menu>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Announcement</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            fullWidth
            label="Title"
            value={title}
            onChange={e => setTitle(e.target.value)}
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            fullWidth
            label="Body"
            value={body}
            onChange={e => setBody(e.target.value)}
            multiline
            rows={6}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateAnnouncement}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
            }}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </CommunityLayout>
  )
}

export default Announcements
